import { useEffect } from 'react';
import { FloatingFormItem } from '@/components/_common';
import {
  useAuthorizeDirectSale,
  useCancelDirectSale,
} from '@/core/domain/sales/DirectSale/DirectSale.hooks';
import {
  DirectSale,
  DirectSaleAuthorizationOrCancelation,
  DirectSaleStatusEnum,
} from '@/core/domain/sales/DirectSale/DirectSale.types';
import { useDisclosure } from '@/hooks';
import { Form, Input, Modal, notification } from 'antd';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import DirectSaleGenerateInvoiceModal from './DirectSaleGenerateInvoiceModal';
import DirectSaleTransmitInvoiceModal from './DirectSaleTransmitInvoiceModal';
import DirectSaleButtonsRules from './DirectSaleButtonsRules';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { DirectSaleModalsFormValues } from '@/core/domain/invoice/invoices/invoices.createTypes';
import { InvoiceDTO } from '@/core/domain/invoice/invoice/Invoice.types';
import ButtonBack from '@/components/_common/ButtonBack';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import DirectSaleTaxCouponButton from './DirectSaleTaxCouponButton';
import { InvoiceSituation } from '@/core/domain/invoice/invoices/invoices.enums';
import ReferencedNotesModal from '@/components/_common/ReferencedNotesModal';
import ReferencedNotesButton from '@/components/_common/ReferencedNotesButton';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import DirectSaleTermGenerateButton from './DirectSaleTermGenerateButton';
import DirectSalePrintModal from './DirectSalePrintModal';

interface Props {
  directSale?: DirectSale;
  invoiceData: InvoiceDTO[];
}

const DirectSaleButtons: React.FC<Props> = ({ directSale, invoiceData }) => {
  const intl = useIntl();

  const history = useHistory();
  const [modalGenerateInvoiceVisible, modalGenerateInvoiceAction] =
    useDisclosure();
  const [transmitInvoiceModalVisible, transmitInvoiceModalAction] =
    useDisclosure();
  const [ReferencedNoteModalVisible, ReferencedNoteModalAction] =
    useDisclosure();
  const [SalePrintModalVisible, SalePrintModalAction] = useDisclosure();

  const [form] = Form.useForm<DirectSaleModalsFormValues>();

  const authorizeMutation = useAuthorizeDirectSale(directSale?.id);
  const cancelMutation = useCancelDirectSale(directSale?.id);

  useEffect(() => {
    if (directSale) {
      form.setFieldsValue({ companyId: directSale.companyId });
    }
  }, [directSale]);

  const handleAuthorizeSale = (status: DirectSaleStatusEnum) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'directSale.notifications.authorizeSale.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'directSale.notifications.authorizeSale.confirm.content',
      })}`,
      onOk: async () => {
        try {
          const obj: DirectSaleAuthorizationOrCancelation = {
            status,
          };

          await authorizeMutation.mutateAsync(obj);
        } catch (err) {
          notification.error({
            message: intl.formatMessage({
              id: 'commons.form.notification.error',
            }),
            description: intl.formatMessage({
              id: 'commons.form.notification.errorDescription',
            }),
          });
        }
      },
      okText: intl.formatMessage({ id: 'commons.options.yes' }),
      okType: 'primary',
      cancelText: intl.formatMessage({ id: 'commons.options.no' }),
    });
  };

  const handleCancelationSale = (status: DirectSaleStatusEnum) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'directSale.cancelSale.modal.title.reasonCancellation',
      }),

      content: (
        <>
          <Form form={form}>
            <FloatingFormItem
              name="reasonCancellation"
              label={intl.formatMessage({
                id: 'directSale.cancelSale.modal.title.reasonCancellation',
              })}
            >
              <Input.TextArea rows={10} maxLength={100} showCount />
            </FloatingFormItem>
          </Form>
          <div style={{ clear: 'both' }} />
        </>
      ),

      onOk: async () => {
        const values = await form?.validateFields();

        try {
          const obj: DirectSaleAuthorizationOrCancelation = {
            status,
            reasonCancellation: values?.reasonCancellation,
          };

          await cancelMutation.mutateAsync(obj);
        } catch (err) {
          notification.error({
            message: intl.formatMessage({
              id: 'commons.form.notification.error',
            }),
            description: intl.formatMessage({
              id: 'commons.form.notification.errorDescription',
            }),
          });
        }
      },
      width: 700,
      okText: intl.formatMessage({ id: 'directSale.button.cancelSale' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'commons.form.button.close' }),
    });
  };

  const baseButtonRules =
    !!directSale?.id &&
    directSale.status !== DirectSaleStatusEnum.CANCELED &&
    !!invoiceData.length;

  const authorizeSaleRules = [
    !!directSale?.id && directSale.status === DirectSaleStatusEnum.CANCELED,
    !!directSale?.id && directSale.status === DirectSaleStatusEnum.AUTHORIZED,
  ];

  const cancelSaleRules = [
    !!directSale?.id && directSale.status === DirectSaleStatusEnum.CANCELED,
    !!invoiceData.length &&
      !invoiceData.every(
        (invoce) => invoce.situation === InvoiceSituation.CANCELED,
      ),
    !!directSale?.id && !!directSale.generatedFinancial,
  ];

  const generateSaleRules = [
    !!directSale?.id && directSale.status !== DirectSaleStatusEnum.AUTHORIZED,
    !!invoiceData.length &&
      !invoiceData.every(
        (invoce) => invoce.situation === InvoiceSituation.CANCELED,
      ),
  ];

  const transmitSaleRules = [
    !invoiceData.length ||
      invoiceData.every(
        (invoce) => invoce.situation === InvoiceSituation.CANCELED,
      ),
    !!directSale?.id && directSale.status === DirectSaleStatusEnum.CANCELED,
    !!directSale?.id && directSale.status !== DirectSaleStatusEnum.AUTHORIZED,
  ];

  const newDirectSaleRules = [!!!directSale?.id];

  const printAndSaleRules = [false];

  const { getSearch } = usePageSearchHistory();
  const path = '/app/movements/directSale';
  const pathWithSearch = path + getSearch(path);

  return (
    <>
      <SpaceForButton hidden={!!!directSale?.id}>
        <ButtonBack path={pathWithSearch} />

        <DirectSaleButtonsRules
          onClick={() => history.push('/app/movements/directSale/create')}
          intl="directSale.create"
          rules={newDirectSaleRules}
          disabled={!hasPermissions([Authorities.MOVIMENTATION_SALE_ADD])}
        />

        <DirectSaleButtonsRules
          onClick={() => handleAuthorizeSale(DirectSaleStatusEnum.AUTHORIZED)}
          intl="directSale.button.authorizeSale"
          rules={authorizeSaleRules}
          disabled={!hasPermissions([Authorities.MOVIMENTATION_SALE_AUTHORIZE])}
        />

        <DirectSaleButtonsRules
          onClick={() => handleCancelationSale(DirectSaleStatusEnum.CANCELED)}
          intl="directSale.button.cancelSale"
          rules={cancelSaleRules}
          disabled={!hasPermissions([Authorities.MOVIMENTATION_SALE_CANCEL])}
          danger
        />

        <DirectSaleButtonsRules
          onClick={modalGenerateInvoiceAction.open}
          intl="directSale.button.generateInvoice"
          rules={generateSaleRules}
          disabled={
            !hasPermissions([Authorities.MOVIMENTATION_SALE_GENERATE_INVOICE])
          }
        />

        <DirectSaleButtonsRules
          onClick={transmitInvoiceModalAction.open}
          intl="directSale.transmitModal.button.transmitInvoice"
          rules={transmitSaleRules}
        />

        <ReferencedNotesButton
          onClick={ReferencedNoteModalAction.open}
          rules={[!invoiceData.length]}
          type="default"
        />

        <DirectSaleTaxCouponButton
          directSale={directSale}
          invoiceData={invoiceData}
        />

        <DirectSaleButtonsRules
          onClick={SalePrintModalAction.open}
          intl="directSale.button.printSale"
          rules={printAndSaleRules}
          key="viewReport"
          disabled={!hasPermissions([Authorities.MOVIMENTATION_SALE_PRINT])}
        />

        {!!printAndSaleRules && (
          <DirectSaleTermGenerateButton directSale={directSale} />
        )}

        <DirectSaleGenerateInvoiceModal
          form={form}
          visible={modalGenerateInvoiceVisible}
          onCancel={modalGenerateInvoiceAction.close}
          directSale={directSale}
        />

        <ReferencedNotesModal
          invoices={invoiceData}
          open={ReferencedNoteModalVisible}
          onCancel={ReferencedNoteModalAction.close}
        />

        {baseButtonRules && (
          <DirectSaleTransmitInvoiceModal
            visible={transmitInvoiceModalVisible}
            onCancel={transmitInvoiceModalAction.close}
            invoiceId={invoiceData[0]?.id}
          />
        )}

        <DirectSalePrintModal
          open={SalePrintModalVisible}
          directSaleId={directSale?.id}
          onCancel={SalePrintModalAction.close}
        />
      </SpaceForButton>
    </>
  );
};

export default DirectSaleButtons;
